import { Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";

export default function CartDrawer() {
  const { items, isOpen, close, removeItem, updateQuantity, totalCount, subtotal } =
    useCart();

  return (
    <>
      {/* Overlay */}
      <div
        onClick={close}
        aria-hidden={!isOpen}
        className={`fixed inset-0 z-[60] bg-foreground-950/40 transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-label="Shopping basket"
        aria-hidden={!isOpen}
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[420px] bg-background-50 flex flex-col transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 md:px-6 h-16 border-b border-background-200/70 shrink-0">
          <h2 className="font-heading text-lg text-foreground-950">
            Cart{totalCount > 0 ? ` (${totalCount})` : ""}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label="Close basket"
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 md:px-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <p className="text-sm text-foreground-700">Your cart is empty.</p>
              <Link
                to="/#collections"
                onClick={close}
                className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-900 cursor-pointer whitespace-nowrap"
              >
                Continue shopping
              </Link>
            </div>
          ) : (
            <ul className="py-4 divide-y divide-background-200/70">
              {items.map((it) => (
                <li key={it.lineId} className="py-4 flex gap-4">
                  <Link
                    to={`/product/${it.productId}`}
                    onClick={close}
                    className="shrink-0 w-16 h-20 rounded-lg overflow-hidden bg-background-100 cursor-pointer"
                  >
                    {it.image ? (
                      <img
                        src={it.image}
                        alt={it.productName}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <span className="w-full h-full flex items-center justify-center text-foreground-500">
                        <i className="ri-image-line"></i>
                      </span>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <Link
                        to={`/product/${it.productId}`}
                        onClick={close}
                        className="text-sm font-medium text-foreground-950 leading-snug hover:text-primary-700 cursor-pointer line-clamp-2"
                      >
                        {it.productName}
                      </Link>
                      <button
                        type="button"
                        onClick={() => removeItem(it.lineId)}
                        aria-label="Remove"
                        className="text-xs text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                    {it.skuLabel && (
                      <span className="text-xs text-foreground-600 mt-1">
                        {it.skuLabel}
                      </span>
                    )}

                    <div className="mt-auto pt-3 flex items-center justify-between gap-3">
                      <div className="inline-flex items-center h-8 rounded-md border border-background-300/80">
                        <button
                          type="button"
                          onClick={() => updateQuantity(it.lineId, it.quantity - 1)}
                          disabled={it.quantity <= 1}
                          aria-label="Decrease"
                          className="w-8 h-8 flex items-center justify-center text-foreground-800 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-foreground-950">
                          {it.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(it.lineId, it.quantity + 1)}
                          disabled={it.quantity >= (it.maxStock || 99)}
                          aria-label="Increase"
                          className="w-8 h-8 flex items-center justify-center text-foreground-800 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                      <p className="text-sm font-medium text-foreground-950">
                        ${(it.finalPrice * it.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-background-200/70 px-5 md:px-6 py-5 space-y-3 shrink-0 bg-background-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground-700">Subtotal</span>
              <span className="text-lg font-medium text-foreground-950">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <Link
              to="/checkout"
              onClick={close}
              className="w-full inline-flex items-center justify-center h-12 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Checkout
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}